import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Loader,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  Title,
  Group,
} from "@mantine/core";
import { IconDownload, IconPlayerPlay } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import api from "../../services/api";

/** Restaurant from API list (id, name, ...) */
interface RestaurantOption {
  id: string;
  name: string | null;
}

/** Menu from GET /restaurants/:id (menus array, sorted by createdAt desc) */
interface MenuOption {
  id: string;
  createdAt: string;
  adminNotes?: string | null;
}

/** One FAQ entry from test-batch result */
interface FaqEntry {
  index: number;
  question: string;
  answer: string;
}

/** One item in test-batch results */
interface FaqTestResult {
  menuItemId: string;
  menuItemName: string;
  error?: string;
  faq: {
    entries: FaqEntry[];
  } | null;
}

/**
 * Escape a value for CSV: wrap in double quotes and escape internal quotes (RFC 4180).
 * Always quotes string fields so commas in answers/questions never break parsing.
 */
function csvEscape(value: string): string {
  const s = String(value ?? "");
  return '"' + s.replace(/"/g, '""') + '"';
}

/**
 * Build CSV string from test-batch results.
 * One row per FAQ entry: menuItemId, menuItemName, questionIndex, question, answer.
 */
function buildFaqResultsCsv(results: FaqTestResult[]): string {
  const header = "menuItemId,menuItemName,questionIndex,question,answer";
  const rows: string[] = [];
  for (const r of results) {
    if (r.error || !r.faq?.entries?.length) {
      rows.push([csvEscape(r.menuItemId), csvEscape(r.menuItemName), "", "", r.error ? csvEscape(r.error) : '""'].join(","));
    } else {
      for (const entry of r.faq.entries) {
        rows.push(
          [
            csvEscape(r.menuItemId),
            csvEscape(r.menuItemName),
            entry.index,
            csvEscape(entry.question),
            csvEscape(entry.answer),
          ].join(",")
        );
      }
    }
  }
  return [header, ...rows].join("\n");
}

/**
 * Admin tab: Menu Item Q&A (FAQ) testing.
 * - Dropdown of restaurants → dropdown of menus for that restaurant (sorted by date created desc).
 * - Execute generates FAQ for the first 5 menu items (dry run).
 * - Results are downloadable as CSV.
 */
export function MenuItemQATesting() {
  const [restaurants, setRestaurants] = useState<RestaurantOption[]>([]);
  const [menus, setMenus] = useState<MenuOption[]>([]);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string | null>(null);
  const [selectedMenuId, setSelectedMenuId] = useState<string | null>(null);
  const [loadingRestaurants, setLoadingRestaurants] = useState(true);
  const [loadingMenus, setLoadingMenus] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [results, setResults] = useState<FaqTestResult[] | null>(null);

  // Load restaurants for dropdown (high pageSize to get all)
  useEffect(() => {
    let cancelled = false;
    setLoadingRestaurants(true);
    api
      .get<{ data: RestaurantOption[] }>("/restaurants", { params: { page: 1, pageSize: 500 } })
      .then((res) => {
        if (!cancelled && Array.isArray(res.data?.data)) {
          setRestaurants(res.data.data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          notifications.show({
            title: "Error loading restaurants",
            message: err.response?.data?.message ?? err.message,
            color: "red",
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingRestaurants(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // When restaurant changes, load its menus (backend returns menus sorted by createdAt desc)
  useEffect(() => {
    if (!selectedRestaurantId) {
      setMenus([]);
      setSelectedMenuId(null);
      return;
    }
    let cancelled = false;
    setLoadingMenus(true);
    setMenus([]);
    setSelectedMenuId(null);
    setResults(null);
    api
      .get<{ restaurant: { menus: MenuOption[] } }>(`/restaurants/${selectedRestaurantId}`)
      .then((res) => {
        if (!cancelled && res.data?.restaurant?.menus) {
          // Backend already returns menus ordered by createdAt DESC
          setMenus(res.data.restaurant.menus);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          notifications.show({
            title: "Error loading menus",
            message: err.response?.data?.message ?? err.message,
            color: "red",
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingMenus(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedRestaurantId]);

  // Clear results when menu selection changes
  useEffect(() => {
    setResults(null);
  }, [selectedMenuId]);

  const handleExecute = useCallback(async () => {
    if (!selectedMenuId) return;
    setExecuting(true);
    setResults(null);
    try {
      const res = await api.post<{ results: FaqTestResult[] }>(
        `/menus/${selectedMenuId}/faqs/test-batch`
      );
      setResults(res.data?.results ?? []);
      notifications.show({
        title: "Done",
        message: `Generated FAQ for first 5 menu items.`,
        color: "green",
      });
    } catch (err: unknown) {
      const message = err && typeof err === "object" && "response" in err
        ? (err as { response?: { data?: { message?: string }; message?: string } }).response?.data?.message
          ?? (err as { message?: string }).message
        : "Request failed";
      notifications.show({
        title: "Error",
        message: String(message),
        color: "red",
      });
    } finally {
      setExecuting(false);
    }
  }, [selectedMenuId]);

  const handleDownloadCsv = useCallback(() => {
    if (!results || results.length === 0) return;
    const csv = buildFaqResultsCsv(results);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `menu-item-faq-results-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [results]);

  const restaurantOptions = restaurants.map((r) => ({
    value: r.id,
    label: r.name || `Restaurant ${r.id}`,
  }));

  const menuOptions = menus.map((m) => ({
    value: m.id,
    label: `Menu ${m.id} (${m.createdAt ? new Date(m.createdAt).toLocaleDateString() : "—"})`,
  }));

  return (
    <Stack gap="lg">
      <Title order={3}>Menu Item FAQ Testing</Title>
      <Text size="sm" c="dimmed">
        Select a restaurant and menu, then run FAQ generation for the first 5 menu items (dry run).
        Results can be downloaded as CSV.
      </Text>

      <Paper p="md" withBorder>
        <Stack gap="md">
          <Select
            label="Restaurant"
            placeholder={loadingRestaurants ? "Loading…" : "Select restaurant"}
            data={restaurantOptions}
            value={selectedRestaurantId}
            onChange={(v) => setSelectedRestaurantId(v)}
            disabled={loadingRestaurants}
            clearable
            searchable
          />
          <Select
            label="Menu"
            placeholder={
              !selectedRestaurantId
                ? "Select a restaurant first"
                : loadingMenus
                  ? "Loading…"
                  : "Select menu (newest first)"
            }
            data={menuOptions}
            value={selectedMenuId}
            onChange={(v) => setSelectedMenuId(v)}
            disabled={!selectedRestaurantId || loadingMenus}
            clearable
            searchable
          />
          <Button
            leftSection={executing ? <Loader size="sm" /> : <IconPlayerPlay size={16} />}
            onClick={handleExecute}
            loading={executing}
            disabled={!selectedMenuId || executing}
          >
            Generate FAQ for first 5 items
          </Button>
        </Stack>
      </Paper>

      {results !== null && (
        <Paper p="md" withBorder>
          <Group justify="space-between" mb="md">
            <Text fw={500}>Results ({results.length} items)</Text>
            <Button
              leftSection={<IconDownload size={16} />}
              variant="light"
              onClick={handleDownloadCsv}
              disabled={results.length === 0}
            >
              Download CSV
            </Button>
          </Group>
          {results.length === 0 ? (
            <Alert color="gray">No results.</Alert>
          ) : (
            <ScrollArea>
              <Table striped withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Menu item</Table.Th>
                    <Table.Th>Q#</Table.Th>
                    <Table.Th>Question</Table.Th>
                    <Table.Th>Answer</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {results.flatMap((r) => {
                    if (r.error) {
                      return (
                        <Table.Tr key={r.menuItemId}>
                          <Table.Td>{r.menuItemName}</Table.Td>
                          <Table.Td>—</Table.Td>
                          <Table.Td colSpan={2}>
                            <Text size="sm" c="red">
                              {r.error}
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      );
                    }
                    return (r.faq?.entries ?? []).map((entry) => (
                      <Table.Tr key={`${r.menuItemId}-${entry.index}`}>
                        <Table.Td>{r.menuItemName}</Table.Td>
                        <Table.Td>{entry.index}</Table.Td>
                        <Table.Td style={{ maxWidth: 280 }}>{entry.question}</Table.Td>
                        <Table.Td style={{ maxWidth: 360 }}>{entry.answer}</Table.Td>
                      </Table.Tr>
                    ));
                  })}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          )}
        </Paper>
      )}
    </Stack>
  );
}
